import { Avatar, Button } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import type { DesignerBioImages, MatchRecord } from "../types";
import {
  formatDistance,
  getCounterpartReaction,
  getCounterpartReactionBadge,
  getDesignerBioLookupId,
  getMatchDisplayId,
  getLocationLabel,
  getProfileImage,
  shouldShowSelectPerfectMatch,
} from "../utils";
import { MatchStatusPill } from "./MatchStatusPill";
import { MatchScoreRing } from "./MatchScoreRing";
import { ReactionButtons } from "./ReactionButtons";
import type { MatchReaction } from "../../../services/matchesApi";

export const MatchesListPanel = ({
  matches,
  selectedMatchId,
  isHomeowner,
  hasActiveMatch,
  selectingMatchId,
  bioImageCache,
  onSelectMatch,
  onReactionChange,
  onSelectPerfectMatch,
  isMatchUnavailable,
  disableInteractions = false,
}: {
  matches: MatchRecord[];
  selectedMatchId: string | null;
  isHomeowner: boolean;
  hasActiveMatch: boolean;
  selectingMatchId: string | null;
  bioImageCache: Record<string, DesignerBioImages>;
  onSelectMatch: (match: MatchRecord) => void;
  onReactionChange: (matchId: string, next: MatchReaction) => void;
  onSelectPerfectMatch: (match: MatchRecord) => void;
  isMatchUnavailable: (match: MatchRecord) => boolean;
  disableInteractions?: boolean;
}) => {
  return (
    <div className="matches-list-panel content-block">
      <div className="matches-list">
        {matches.length ? (
          matches.map((match) => {
            const isSelected = match.matchId === selectedMatchId;
            const isUnavailable = isMatchUnavailable(match);
            const showSelectPerfectMatch = shouldShowSelectPerfectMatch({
              match,
              isHomeowner,
              hasActiveMatch,
            }) && !disableInteractions;
            const isSelecting = selectingMatchId === match.matchId;
            const profileId = getDesignerBioLookupId(match);
            const profileImages = profileId ? bioImageCache[profileId] : null;
            const profileImage = getProfileImage(match, profileImages, null);
            const displayMatchId = getMatchDisplayId(
              match.matchId,
              isHomeowner ? "homeowner" : "designer"
            );
            const counterpartReactionBadge = getCounterpartReactionBadge(
              getCounterpartReaction(match)
            );
            const showMatchMade = Boolean(match.isMatchMade);
            return (
              <div
                key={match.matchId}
                className={`matches-list-item ${isSelected ? "is-selected" : ""} ${
                  isUnavailable ? "is-unavailable" : ""
                }`}
                onClick={() => onSelectMatch(match)}
                aria-disabled={isUnavailable}
                role="button"
                tabIndex={isUnavailable ? -1 : 0}
                onKeyDown={(event) => {
                  if (isUnavailable) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectMatch(match);
                  }
                }}
              >
                <div className="matches-avatar">
                  <Avatar src={profileImage || undefined}>
                    {(match.name ?? displayMatchId).charAt(0)}
                  </Avatar>
                  <span
                    className={`status-dot ${match.isOnline ? "online" : "offline"}`}
                  />
                </div>
                <div className="matches-list-info">
                  <div className="matches-list-title">{displayMatchId}</div>
                  <div className="matches-list-meta">
                    <span>
                      <PlaceOutlinedIcon />
                      {getLocationLabel(match)}
                    </span>
                    <span>
                      <RouteOutlinedIcon />
                      {formatDistance(match.distanceMiles)}
                    </span>
                  </div>
                  {counterpartReactionBadge && (
                    <MatchStatusPill
                      tone={counterpartReactionBadge.tone}
                      label={counterpartReactionBadge.label}
                      className="matches-list-counterpart-reaction"
                    />
                  )}
                  {!match.isOnline && (
                    <span className="matches-list-status offline">Offline</span>
                  )}
                </div>
                <div className="matches-list-actions">
                  <div className="matches-list-reaction-row">
                    {showMatchMade ? (
                      <Button
                        variant="contained"
                        className="matches-select-match matches-select-match--inline matches-select-match--made"
                        disabled
                      >
                        Match Made
                      </Button>
                    ) : (
                      <>
                        {showSelectPerfectMatch && (
                          <Button
                            variant="outlined"
                            className="matches-select-match matches-select-match--inline"
                            disabled={isSelecting}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSelectPerfectMatch(match);
                            }}
                          >
                            {isSelecting ? "Selecting..." : "Select as Perfect Match"}
                          </Button>
                        )}
                        <ReactionButtons
                          current={match.reaction ?? null}
                          onChange={(next) => onReactionChange(match.matchId, next)}
                          disabled={disableInteractions || isUnavailable}
                          isMutualLove={Boolean(match.isMutualLove)}
                        />
                      </>
                    )}
                  </div>
                  <MatchScoreRing value={match.matchScore} size="small" />
                </div>
              </div>
            );
          })
        ) : (
          <div className="matches-empty-filter">No matches for this filter.</div>
        )}
      </div>
    </div>
  );
};
